export interface StoredShot {
  made: boolean;
  value: 0 | 2 | 3;
  passes: number;
}

export interface StoredResult {
  score: number;
  timeLeftTenths: number;
  tier: string;
  journey: StoredShot[];
  attempts: number;
}

export interface Profile {
  version: 1;
  firstSeen: string;
  lastPlayed?: string;
  gamesPlayed: number;
  currentStreak: number;
  bestStreak: number;
  graceAvailable: boolean;
  graceNeedsEarn: boolean;
  helpSeen: boolean;
  muted: boolean;
  results: Record<string, StoredResult>;
}

export interface CompletionUpdate {
  profile: Profile;
  isBest: boolean;
  previousBest?: StoredResult;
  streakRepaired: boolean;
  streakBroken?: { previous: number; best: number };
}

const STORAGE_KEY = 'closeout_profile_v1';

export function utcDateKey(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

export function puzzleNumber(dateKey: string, epoch = '2026-08-18'): number {
  return Math.floor((Date.parse(`${dateKey}T00:00:00Z`) - Date.parse(`${epoch}T00:00:00Z`)) / 86_400_000) + 1;
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function loadProfile(today = utcDateKey()): Profile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return freshProfile(today);
    const parsed = JSON.parse(raw) as Partial<Profile>;
    if (parsed.version !== 1 || typeof parsed.firstSeen !== 'string') return freshProfile(today);
    return {
      version: 1,
      firstSeen: parsed.firstSeen,
      lastPlayed: parsed.lastPlayed,
      gamesPlayed: parsed.gamesPlayed ?? 0,
      currentStreak: parsed.currentStreak ?? 0,
      bestStreak: parsed.bestStreak ?? 0,
      graceAvailable: parsed.graceAvailable ?? true,
      graceNeedsEarn: parsed.graceNeedsEarn ?? false,
      helpSeen: parsed.helpSeen ?? false,
      muted: parsed.muted ?? false,
      results: parsed.results ?? {},
    };
  } catch {
    return freshProfile(today);
  }
}

export function saveProfile(profile: Profile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prune(profile)));
  } catch {
    // Storage is best-effort. Core play never depends on it.
  }
}

export function markHelpSeen(profile: Profile): Profile {
  const next = { ...profile, helpSeen: true };
  saveProfile(next);
  return next;
}

export function setMuted(profile: Profile, muted: boolean): Profile {
  const next = { ...profile, muted };
  saveProfile(next);
  return next;
}

export function recordCompletion(profile: Profile, dateKey: string, result: Omit<StoredResult, 'attempts'>): CompletionUpdate {
  const previousBest = profile.results[dateKey];
  const attempts = (previousBest?.attempts ?? 0) + 1;
  const candidate: StoredResult = { ...result, attempts };
  const isBest = !previousBest || compareResults(candidate, previousBest) > 0;
  const results = { ...profile.results, [dateKey]: isBest ? candidate : { ...previousBest, attempts } };

  let currentStreak = profile.currentStreak;
  let bestStreak = profile.bestStreak;
  let graceAvailable = profile.graceAvailable;
  let graceNeedsEarn = profile.graceNeedsEarn;
  let gamesPlayed = profile.gamesPlayed;
  let streakRepaired = false;
  let streakBroken: CompletionUpdate['streakBroken'];

  if (profile.lastPlayed !== dateKey) {
    gamesPlayed += 1;
    if (!profile.lastPlayed) {
      currentStreak = 1;
    } else {
      const gap = daysBetween(profile.lastPlayed, dateKey);
      if (gap <= 1) {
        currentStreak = Math.max(1, currentStreak + 1);
        if (graceNeedsEarn) {
          graceAvailable = true;
          graceNeedsEarn = false;
        }
      } else if (gap === 2 && graceAvailable) {
        currentStreak = Math.max(1, currentStreak + 1);
        graceAvailable = false;
        graceNeedsEarn = true;
        streakRepaired = true;
      } else {
        if (currentStreak > 0) streakBroken = { previous: currentStreak, best: Math.max(bestStreak, currentStreak) };
        currentStreak = 1;
        graceAvailable = true;
        graceNeedsEarn = false;
      }
    }
    bestStreak = Math.max(bestStreak, currentStreak);
  }

  const next: Profile = {
    ...profile,
    lastPlayed: dateKey,
    gamesPlayed,
    currentStreak,
    bestStreak,
    graceAvailable,
    graceNeedsEarn,
    results,
  };
  saveProfile(next);
  return { profile: next, isBest, previousBest, streakRepaired, streakBroken };
}

export function compareResults(a: Pick<StoredResult, 'score' | 'timeLeftTenths'>, b: Pick<StoredResult, 'score' | 'timeLeftTenths'>): number {
  return a.score === b.score ? a.timeLeftTenths - b.timeLeftTenths : a.score - b.score;
}

function freshProfile(today: string): Profile {
  return {
    version: 1,
    firstSeen: today,
    gamesPlayed: 0,
    currentStreak: 0,
    bestStreak: 0,
    graceAvailable: true,
    graceNeedsEarn: false,
    helpSeen: false,
    muted: false,
    results: {},
  };
}

function prune(profile: Profile): Profile {
  const keys = Object.keys(profile.results).sort().slice(-45);
  return { ...profile, results: Object.fromEntries(keys.map((key) => [key, profile.results[key]!])) };
}
